import {
  Autocomplete,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import AsyncLoadingButton from "../../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../../components/cancelButton/CancelButton";
import dayjs from "../../../dayjs";
import { ISO_DATE_FORMAT } from "../../../dayjs";
import { useOrders } from "../../useOrders";
import type { CancelProductDialogItem } from "./CancelProductDialog";

interface OrderOption {
  readonly id: string;
  readonly name: string;
  readonly userFriendlyId: number;
}

interface TargetOrderSelectProps {
  readonly value: string;
  readonly orders: OrderOption[];
  readonly isLoading: boolean;
  readonly onChange: (orderId: string) => void;
}

const TargetOrderSelect: FC<TargetOrderSelectProps> = ({ value, orders, isLoading, onChange }) => {
  const { options, selectedOption } = useMemo(() => {
    const options = orders.map((o) => ({
      value: o.id,
      label: `${o.userFriendlyId}: ${o.name}`,
    }));
    const selectedOption = options.find((o) => o.value === value) ?? null;
    return { options, selectedOption };
  }, [orders, value]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      disabled={isLoading}
      value={selectedOption}
      renderInput={(params) => <TextField {...params} label="Zamówienie docelowe" placeholder="Wybierz zamówienie" />}
      options={options}
      isOptionEqualToValue={(o, v) => o?.value === v?.value}
      getOptionKey={(o) => o.value}
      getOptionLabel={(o) => o.label}
      onChange={(_, selected) => onChange(selected?.value ?? "")}
    />
  );
};

interface MoveProductDialogProps {
  readonly open: boolean;
  readonly products: CancelProductDialogItem[];
  readonly currentOrderId: string;
  readonly onConfirm: (selectedIndexes: number[], targetOrderId: string) => Promise<void>;
  readonly onCancel: () => void;
}

const MoveProductDialog: FC<MoveProductDialogProps> = ({ open, products, currentOrderId, onConfirm, onCancel }) => {
  const [targetOrderId, setTargetOrderId] = useState("");
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());

  const { orders, isLoading } = useOrders({ from: dayjs().format(ISO_DATE_FORMAT), showHistorical: false });

  const candidateOrders = orders.filter((o) => o.id !== currentOrderId);

  useEffect(() => {
    if (open) {
      setTargetOrderId("");
    }
  }, [open]);

  useEffect(() => {
    if (open && products.length > 0) {
      setSelectedIndexes(new Set(products.map((p) => p.index)));
    }
  }, [open, products]);

  const handleToggle = (index: number) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedIndexes(checked ? new Set(products.map((p) => p.index)) : new Set());
  };

  const handleConfirm = async () => {
    if (targetOrderId && selectedIndexes.size > 0) {
      await onConfirm(
        [...selectedIndexes].sort((a, b) => a - b),
        targetOrderId,
      );
    }
  };

  const allSelected = products.length > 0 && selectedIndexes.size === products.length;
  const someSelected = selectedIndexes.size > 0;

  return (
    <Dialog open={open} maxWidth="lg" fullWidth aria-labelledby="move-product-dialog-title">
      <DialogTitle id="move-product-dialog-title">Przenieś produkty do zamówienia</DialogTitle>
      <DialogContent>
        <Stack
          spacing={2}
          sx={{
            marginTop: 1,
          }}
        >
          <Typography>
            Wybierz produkty z tej samej rezerwacji, które mają zostać przeniesione, oraz zamówienie docelowe.
          </Typography>
          {products.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={(_, checked) => handleSelectAll(checked)}
                      aria-label="Zaznacz wszystkie"
                    />
                  </TableCell>
                  <TableCell>Produkt</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map(({ index, name, date }) => (
                  <TableRow key={index}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIndexes.has(index)}
                        onChange={() => handleToggle(index)}
                        aria-label={`Zaznacz ${name}`}
                      />
                    </TableCell>
                    <TableCell>{name}</TableCell>
                    <TableCell>{date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {isLoading && <LinearProgress />}
          <TargetOrderSelect
            value={targetOrderId}
            orders={candidateOrders}
            isLoading={isLoading}
            onChange={setTargetOrderId}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onCancel} />
        <AsyncLoadingButton onClick={handleConfirm} disabled={!targetOrderId || !someSelected}>
          Przenieś
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default MoveProductDialog;
